import { useState } from "react";

import { useHistory } from "react-router-dom";
import { Box } from "@mui/material";

import { useCustomerId } from "../../../../Components/hooks/useCustomerId";
import { useErrorSnackbar } from "../../../../Components/SharedSnackbar/SharedSnackbar.context";
import { Stepper } from "../../../../Components/Stepper";
import { type Step } from "../../../../Components/Stepper/Types";
import { useAuthContext } from "../../../../Context/AuthContext";
import { useUserContext } from "../../../../Context/UserContext";
import { customersRoute } from "../../../../Navigation/core/utils";
import AdminKeyStep from "./Steps/AdminKeyStep";
import TestConnectionStep from "./Steps/TestConnectionStep";
import { type FormState } from "./types";

export default function OpenaiOnboarding() {
  const history = useHistory();
  const customerId = useCustomerId();
  const { userRoles } = useUserContext();
  const { isDoitEmployee } = useAuthContext();
  const hasPermissionToOnboard = userRoles?.billingProfilesAdmin || isDoitEmployee;
  const showErrorSnackbar = useErrorSnackbar();

  const [formState, setFormState] = useState<FormState>({
    adminKey: "",
    connectionTestResult: null,
    connectionError: "",
    loading: false,
    successMessage: "",
  });

  const returnToHomePage = () => {
    history.push(customersRoute(customerId));
  };

  const handleChange = (field: "organizationId" | "adminKey", value: string) => {
    setFormState({
      ...formState,
      [field]: value,
      connectionTestResult: null,
      connectionError: "",
    });
  };

  const steps: Step[] = [
    {
      children: (
        <AdminKeyStep
          adminKey={formState.adminKey}
          onAdminKeyChange={(value: string) => {
            handleChange("adminKey", value);
          }}
        />
      ),
      label: "Admin Key",
      order: 0,
      required: true,
      state: formState.adminKey !== "" ? "complete" : "incomplete",
    },
    {
      children: <TestConnectionStep formState={formState} onChange={setFormState} />,
      label: "Test Connection",
      order: 1,
      required: true,
      state: formState.connectionTestResult && !formState.loading ? "complete" : "incomplete",
    },
  ];

  return (
    <Box mt={6} mb={9}>
      <Stepper
        loading={formState.loading}
        onCancel={returnToHomePage}
        onSubmit={() => {
          returnToHomePage();
        }}
        nextButtonClickHandler={async () => {
          if (!hasPermissionToOnboard) {
            showErrorSnackbar("Permission Denied: Billing Profile Admin role required");
            return false;
          }
          return true;
        }}
        steps={steps}
        submitButtonLabel={formState.connectionTestResult ? "Finish" : undefined}
        contentSx={{
          mt: 4,
          mb: 3,
        }}
        footerMaxWidth={860}
        maxWidth={1600}
      />
    </Box>
  );
}
